import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { Incident, IncidentStatus, Page, Severity } from '../api/types'
import {
  Card,
  Empty,
  ErrorNote,
  Labels,
  SeverityBadge,
  Spinner,
  StatusBadge,
  TableScroll,
  since,
} from '../components/ui'
import { useAction, useQuery } from '../hooks/useApi'
import { useIncidentStream } from '../hooks/useIncidentStream'
import { useAuth } from '../auth/AuthContext'

const STATUSES: IncidentStatus[] = [
  'firing',
  'escalated',
  'acknowledged',
  'silenced',
  'suppressed',
  'resolved',
]
const SEVERITIES: Severity[] = ['critical', 'high', 'warning', 'info']
const PAGE_SIZE = 25

export function IncidentsPage() {
  const { can } = useAuth()
  const [statuses, setStatuses] = useState<IncidentStatus[]>(['firing', 'escalated', 'acknowledged'])
  const [severities, setSeverities] = useState<Severity[]>([])
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)

  const query = useQuery(
    () =>
      api.get<Page<Incident>>('/incidents', {
        status: statuses,
        severity: severities,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    [statuses.join(','), severities.join(','), search, offset],
  )
  const action = useAction()

  useIncidentStream(useCallback(() => query.reload(), [query.reload]))

  const toggle = <T,>(list: T[], value: T, setter: (next: T[]) => void) => {
    setOffset(0)
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value])
  }

  const act = async (incident: Incident, what: 'ack' | 'resolve' | 'silence') => {
    const body = what === 'silence' ? { duration_minutes: 60, message: '' } : { message: '' }
    if (await action.run(() => api.post(`/incidents/${incident.id}/${what}`, body))) query.reload()
  }

  const items = query.data?.items ?? []
  const total = query.data?.total ?? 0

  return (
    <>
      <ErrorNote>{query.error ?? action.error}</ErrorNote>

      <Card title="Фильтры">
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((status) => (
              <button
                key={status}
                onClick={() => toggle(statuses, status, setStatuses)}
                className={`btn text-xs ${
                  statuses.includes(status) ? 'bg-sky-600 text-white' : 'btn-ghost'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {SEVERITIES.map((severity) => (
              <button
                key={severity}
                onClick={() => toggle(severities, severity, setSeverities)}
                className={`btn text-xs ${
                  severities.includes(severity) ? 'bg-sky-600 text-white' : 'btn-ghost'
                }`}
              >
                {severity}
              </button>
            ))}
            <input
              className="input w-full sm:w-auto sm:max-w-xs sm:ml-auto"
              placeholder="Поиск по заголовку"
              value={search}
              onChange={(event) => {
                setOffset(0)
                setSearch(event.target.value)
              }}
            />
          </div>
        </div>
      </Card>

      <Card title={`Инциденты — ${total}`}>
        {query.loading && !query.data ? (
          <Spinner />
        ) : items.length === 0 ? (
          <Empty>Ничего не найдено под выбранные фильтры.</Empty>
        ) : (
          <TableScroll minWidth={780}>
            <thead>
              <tr>
                <th className="th">Severity</th>
                <th className="th">Инцидент</th>
                <th className="th">Статус</th>
                <th className="th">Метки</th>
                <th className="th">Возраст</th>
                <th className="th">Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((incident) => (
                <tr key={incident.id} className="hover:bg-ink-700/40">
                  <td className="td">
                    <SeverityBadge severity={incident.severity} />
                  </td>
                  <td className="td">
                    <Link className="text-sky-300 hover:underline" to={`/incidents/${incident.id}`}>
                      {incident.title}
                    </Link>
                    <div className="text-xs text-slate-500 mt-0.5">
                      алертов: {incident.alerts_count} · уровень эскалации:{' '}
                      {incident.escalation_level}
                    </div>
                  </td>
                  <td className="td">
                    <StatusBadge status={incident.status} />
                  </td>
                  <td className="td">
                    <Labels labels={incident.labels} limit={3} />
                  </td>
                  <td className="td tabular-nums text-slate-400">{since(incident.started_at)}</td>
                  <td className="td">
                    <div className="flex gap-1">
                      {can('alerts.ack') && incident.status !== 'acknowledged' && incident.status !== 'resolved' && (
                        <button className="btn-ghost text-xs" onClick={() => act(incident, 'ack')}>
                          Ack
                        </button>
                      )}
                      {can('alerts.silence') && incident.status !== 'silenced' && incident.status !== 'resolved' && (
                        <button
                          className="btn-ghost text-xs"
                          onClick={() => act(incident, 'silence')}
                        >
                          Silence 1ч
                        </button>
                      )}
                      {can('alerts.resolve') && incident.status !== 'resolved' && (
                        <button
                          className="btn-ghost text-xs"
                          onClick={() => act(incident, 'resolve')}
                        >
                          Закрыть
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableScroll>
        )}

        <div className="flex items-center justify-between px-4 py-3 text-sm text-slate-400">
          <span>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} из {total}
          </span>
          <div className="flex gap-2">
            <button
              className="btn-ghost"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Назад
            </button>
            <button
              className="btn-ghost"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Вперёд
            </button>
          </div>
        </div>
      </Card>
    </>
  )
}
